import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { InsightBar } from '../../components/charts/InsightBar.jsx';
import { useChartPalette } from '../../components/charts/useChartPalette.js';
import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { YearCloseModal } from './YearCloseModal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { hrApi } from '../../services/hrApi.js';
import { formatDate, formatNumber } from '../../utils/formatters.js';
import { countBy, sumBy } from '../../utils/insightStats.js';

import { LeaveRequestModal } from './LeaveRequestModal.jsx';
import { EntitlementModal } from './EntitlementModal.jsx';
import { LeaveDecisionModal } from './LeaveDecisionModal.jsx';
import { leaveTypeLabel } from '../../utils/leaveTypes.js';
import { LeaveCalendarGrid } from './LeaveCalendarGrid.jsx';

const STATUS_VARIANT = { PENDING: 'warning', APPROVED: 'success', REJECTED: 'danger', CANCELLED: 'neutral' };
const LEAVE_STATUS_ORDER = ['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'];

/* Das Jahr darf nicht im Code stehen: mit einer festen 2026 zeigte die Seite
   ab dem 1.1.2027 stumm die Vorjahreszahlen. */
const CURRENT_YEAR = new Date().getFullYear();

function leaveStatusColor(palette, status) {
  if (status === 'PENDING') return palette?.series?.[2];
  if (status === 'APPROVED') return palette?.series?.[1];
  if (status === 'REJECTED') return palette?.expense;
  if (status === 'CANCELLED') return palette?.axis;
  return palette?.axis;
}

function RequestsTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  /* "Zeig mir nur die offenen" ist der häufigste Handgriff der
     Personalabteilung - und war bis jetzt unmöglich, obwohl das Rückgrat
     den Filter längst konnte. Die Oberfläche schickte schlicht keinen. */
  const [statusFilter, setStatusFilter] = useState('');

  const abfrage = useQuery({
    queryKey: ['hr', 'leave-requests', statusFilter],
    queryFn: () =>
      hrApi.listLeaveRequests({ ...(statusFilter ? { status: statusFilter } : {}), limit: 200 }),
  });
  const data = abfrage.data?.items || [];
  const { isLoading, error } = abfrage;
  const meta = abfrage.data?.meta;

  const palette = useChartPalette();
  /**
   * Die Kennzahlen kommen aus der ZÄHLUNG des Dienstes, nicht aus den
   * geladenen Zeilen.
   *
   * Vorher rechnete ein useMemo über genau das data-Array - also über
   * höchstens 50 Zeilen. Ab dem 51. Antrag zeigte "Anträge" stumm 50, und
   * "Offen" zählte nur innerhalb dieses Fensters. Die Zahl war nicht
   * ungenau, sie war falsch, und nichts wies darauf hin.
   */
  const stats = useMemo(() => {
    const rows = data;
    const server = meta?.stats;
    const counts = server
      ? Object.fromEntries(Object.entries(server.byStatus || {}).map(([s, v]) => [s, v.count]))
      : countBy(rows, (r) => r.status);
    const statuses = [
      ...LEAVE_STATUS_ORDER.filter((s) => counts[s]),
      ...Object.keys(counts).filter((s) => !LEAVE_STATUS_ORDER.includes(s)),
    ];
    return {
      requests: server ? server.total : rows.length,
      statuses,
      counts,
      pending: counts.PENDING || 0,
      approved: counts.APPROVED || 0,
      days: server ? server.days : sumBy(rows, (r) => r.days),
    };
  }, [data, meta]);

  // Keine Entscheidung mehr ohne Rückfrage: ein Fehlklick auf Ablehnen war
  // vorher endgültig und unbegründet.
  const [pending, setPending] = useState(null);

  const decide = useMutation({
    mutationFn: ({ id, decision, decisionNote }) => hrApi.decideLeave(id, decision, decisionNote),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'leave-requests'] });
      queryClient.invalidateQueries({ queryKey: ['hr', 'leave-balances'] });
      queryClient.invalidateQueries({ queryKey: ['hr', 'leave-calendar'] });
    },
  });

  const columns = [
    {
      key: 'employeeId',
      label: t('hr.leave.col.employee'),
      render: (r) => r.employeeName || r.employeeId?.slice(0, 8) || '-',
    },
    { key: 'leaveType', label: t('hr.leave.col.type'), render: (r) => leaveTypeLabel(t, r) },
    { key: 'startDate', label: t('hr.leave.col.start'), render: (r) => formatDate(r.startDate) },
    { key: 'endDate', label: t('hr.leave.col.end'), render: (r) => formatDate(r.endDate) },
    { key: 'days', label: t('hr.leave.col.days'), align: 'right', render: (r) => r.days },
    { key: 'status', label: t('col.status'), render: (r) => <Badge variant={STATUS_VARIANT[r.status]}>{t(`status.${r.status}`)}</Badge> },
    {
      key: 'actions',
      label: '',
      render: (r) =>
        r.status === 'PENDING' ? (
          <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
            <Button size="sm" variant="primary" onClick={() => setPending({ decision: 'APPROVE', request: r })}>
              {t('hr.leave.action.approve')}
            </Button>
            <Button size="sm" variant="danger" onClick={() => setPending({ decision: 'REJECT', request: r })}>
              {t('hr.leave.action.reject')}
            </Button>
          </div>
        ) : r.status === 'APPROVED' ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            {/* Eine genehmigte Abwesenheit liess sich bisher nur über die
                Datenbank zurückziehen, obwohl der Dienst es längst konnte. */}
            <Button size="sm" variant="ghost" onClick={() => setPending({ decision: 'CANCEL', request: r })}>
              {t('hr.leave.action.cancel')}
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <>
      <LeaveDecisionModal
        pending={pending}
        isPending={decide.isPending}
        onClose={() => setPending(null)}
        onConfirm={(decisionNote) => {
          decide.mutate({ id: pending.request.id, decision: pending.decision, decisionNote });
          setPending(null);
        }}
      />
      {stats.requests > 0 && (
        <InsightBar
          title={t('insight.byStatus')}
          labels={stats.statuses.map((s) => (s === '-' ? s : t(`status.${s}`)))}
          values={stats.statuses.map((s) => stats.counts[s])}
          colors={stats.statuses.map((s) => leaveStatusColor(palette, s))}
          metrics={[
            { label: t('hr.leave.insight.requests'), value: stats.requests },
            { label: t('insight.open'), value: stats.pending },
            { label: t('status.APPROVED'), value: stats.approved },
            { label: t('hr.leave.insight.days'), value: formatNumber(stats.days, 1) },
          ]}
        />
      )}
      {error && <div className="alert danger">{error.message}</div>}
      <div className="week-nav">
        <label className="field" style={{ maxWidth: '14rem', marginBottom: 0 }}>
          <span className="field-label">{t('hr.leave.filter.status')}</span>
          <select
            className="select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">{t('hr.leave.filter.all')}</option>
            {LEAVE_STATUS_ORDER.map((s) => (
              <option key={s} value={s}>
                {t(`status.${s}`)}
              </option>
            ))}
          </select>
        </label>
        {/* Wie viele es GIBT, nicht wie viele gerade geladen sind. Steht
            bewusst daneben: sonst wäre nicht erkennbar, dass die Tabelle
            nur einen Ausschnitt zeigt. */}
        {meta?.total !== undefined && (
          <span className="muted text-sm" style={{ marginLeft: 'auto' }}>
            {t('hr.leave.filter.count', { shown: data.length, total: meta.total })}
          </span>
        )}
      </div>

      <Table columns={columns} rows={data} isLoading={isLoading} emptyMessage={t('empty.leave')} />
    </>
  );
}

function BalancesTab() {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ['hr', 'leave-balances'],
    queryFn: () => hrApi.listLeaveBalances({ year: CURRENT_YEAR }).then((r) => r.items || []),
  });

  const columns = [
    { key: 'employeeName', label: t('hr.leave.col.employee'), render: (r) => r.employeeName || '-' },
    { key: 'leaveTypeName', label: t('hr.leave.col.type'), render: (r) => (r.leaveTypeCode ? leaveTypeLabel(t, r) : t('hr.leave.entitlement.allTypes')) },
    { key: 'year', label: t('hr.leave.entitlement.year') },
    { key: 'entitledDays', label: t('hr.leave.balance.entitled'), align: 'right', render: (r) => formatNumber(r.entitledDays, 1) },
    { key: 'usedDays', label: t('hr.leave.balance.used'), align: 'right', render: (r) => formatNumber(r.usedDays, 1) },
    {
      key: 'carriedOverDays',
      label: t('hr.leave.col.carriedOver'),
      align: 'right',
      /* Der Übertrag aus dem Vorjahr. Ohne diese Spalte war er eine Zahl in
         der Datenbank, die im Rest zwar mitzählt, aber nirgends erklärt,
         warum jemand mehr hat als seinen Jahresanspruch. */
      render: (r) => (Number(r.carriedOverDays) > 0 ? formatNumber(r.carriedOverDays, 1) : '-'),
    },
    {
      key: 'remainingDays',
      label: t('hr.leave.balance.remaining'),
      align: 'right',
      render: (r) => <strong>{formatNumber(r.remainingDays, 1)}</strong>,
    },
  ];

  return <Table columns={columns} rows={data} isLoading={isLoading} emptyMessage={t('hr.leave.balance.empty')} />;
}

function CalendarTab() {
  const { t } = useTranslation();
  const heute = new Date();
  const [jahr, setJahr] = useState(heute.getFullYear());
  const [monat, setMonat] = useState(heute.getMonth() + 1);
  /* Offene Anträge sind DABEI, und zwar standardmässig. Ohne sie sieht der
     Genehmigende nicht, dass zwei Kollegen für dieselbe Woche schon
     beantragt haben - genau die Auskunft, wegen der man den Kalender
     öffnet. Ausblenden kann man sie trotzdem: manchmal will man nur den
     verbindlichen Stand. */
  const [mitOffenen, setMitOffenen] = useState(true);

  const raster = useQuery({
    queryKey: ['hr', 'leave-calendar', jahr, monat, mitOffenen],
    queryFn: () => hrApi.leaveCalendarMonth({ year: jahr, month: monat, includePending: mitOffenen }),
  });

  function blättern(schritte) {
    let m = monat + schritte;
    let j = jahr;
    while (m > 12) {
      m -= 12;
      j += 1;
    }
    while (m < 1) {
      m += 12;
      j -= 1;
    }
    setMonat(m);
    setJahr(j);
  }

  return (
    <div className="leave-calendar-month">
      <div className="week-nav">
        <Button size="sm" variant="ghost" onClick={() => blättern(-1)}>
          {t('time.week.prev')}
        </Button>
        <span className="mono">
          {t(`month.name.${monat}`)} {jahr}
        </span>
        <Button size="sm" variant="ghost" onClick={() => blättern(1)}>
          {t('time.week.next')}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            const n = new Date();
            setJahr(n.getFullYear());
            setMonat(n.getMonth() + 1);
          }}
        >
          {t('hr.leave.calendar.thisMonth')}
        </Button>
        <label className="pf-role-option" style={{ marginLeft: 'auto' }}>
          <input
            type="checkbox"
            checked={mitOffenen}
            onChange={(e) => setMitOffenen(e.target.checked)}
          />
          <span>{t('hr.leave.calendar.showPending')}</span>
        </label>
      </div>

      <LeaveCalendarGrid data={raster.data} isLoading={raster.isLoading} />
    </div>
  );
}

export function LeavePage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState('requests');
  const [modalOpen, setModalOpen] = useState(false);
  const [entitlementOpen, setEntitlementOpen] = useState(false);
  const [yearCloseOpen, setYearCloseOpen] = useState(false);

  const tabs = [
    { key: 'requests', label: t('hr.leave.tab.requests') },
    { key: 'balances', label: t('hr.leave.tab.balances') },
    { key: 'calendar', label: t('hr.leave.tab.calendar') },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('hr.leave.title')}</h1>
          <p className="muted">{t('hr.leave.subtitle')}</p>
        </div>
        <div className="page-actions">
          {tab === 'balances' && (
            <>
              <Button variant="default" onClick={() => setEntitlementOpen(true)}>
                {t('hr.leave.entitlement.action')}
              </Button>
              {/* Ohne diesen Vorgang verschwanden Restferien am 1. Januar. */}
              <Button variant="default" onClick={() => setYearCloseOpen(true)}>
                {t('hr.leave.close.title')}
              </Button>
            </>
          )}
          <Button variant="primary" onClick={() => setModalOpen(true)}>
            {t('hr.leave.action.new')}
          </Button>
        </div>
      </header>

      <div className="tab-bar">
        {tabs.map((tabItem) => (
          <button
            key={tabItem.key}
            type="button"
            className={`tab${tab === tabItem.key ? ' tab-active' : ''}`}
            onClick={() => setTab(tabItem.key)}
          >
            {tabItem.label}
          </button>
        ))}
      </div>

      {tab === 'requests' && <RequestsTab />}
      {tab === 'balances' && <BalancesTab />}
      {tab === 'calendar' && <CalendarTab />}

      <LeaveRequestModal open={modalOpen} onClose={() => setModalOpen(false)} />
      <EntitlementModal open={entitlementOpen} onClose={() => setEntitlementOpen(false)} />
      <YearCloseModal
        open={yearCloseOpen}
        onClose={() => setYearCloseOpen(false)}
        year={CURRENT_YEAR}
      />

    </section>
  );
}
